import React, { useRef, useState, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import CreatorProgressModal from './CreatorProgressModal';
import './FullCalendarView.css';

function FullCalendarView({ creatorData = [] }) {
  const calendarRef = useRef(null);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [showCreatorModal, setShowCreatorModal] = useState(false);
  const [dayEventsModalOpen, setDayEventsModalOpen] = useState(false);
  const [selectedDayEvents, setSelectedDayEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [selectedBrief, setSelectedBrief] = useState('');
  const [activeView, setActiveView] = useState('calendar'); // 'calendar' or 'dueDates'

  // Get unique campaigns and briefs for filtering
  const campaigns = useMemo(() => {
    const uniqueCampaigns = [...new Set(creatorData.map(creator => creator.campaignName))];
    return uniqueCampaigns.filter(Boolean).sort();
  }, [creatorData]);

  const briefs = useMemo(() => {
    if (!selectedCampaign) return [];
    const campaignCreators = creatorData.filter(creator => creator.campaignName === selectedCampaign);
    const uniqueBriefs = [...new Set(campaignCreators.map(creator => creator.briefName))];
    return uniqueBriefs.filter(Boolean).sort();
  }, [creatorData, selectedCampaign]);

  // Filter creator data based on selected campaign and brief
  const filteredCreatorData = useMemo(() => {
    let filtered = creatorData;
    
    if (selectedCampaign) {
      filtered = filtered.filter(creator => creator.campaignName === selectedCampaign);
    }
    
    if (selectedBrief) {
      filtered = filtered.filter(creator => creator.briefName === selectedBrief);
    }
    
    return filtered;
  }, [creatorData, selectedCampaign, selectedBrief]);

  // Convert creator data to calendar events
  const events = useMemo(() => {
    const calendarEvents = [];
    
    filteredCreatorData.forEach(creator => {
      // Use actual deadline date if available, otherwise calculate from due text
      let eventDate = new Date();
      
      // Prioritize extendedDeadline, then deadline, then parse due text
      if (creator.extendedDeadline) {
        eventDate = new Date(creator.extendedDeadline);
      } else if (creator.deadline) {
        eventDate = new Date(creator.deadline);
      } else {
        // Fallback: Parse due date text (e.g., "3 Days" -> 3 days from now)
        const dueText = creator.due || '';
        if (dueText.includes('Day')) {
          const days = parseInt(dueText.split(' ')[0]);
          if (!isNaN(days)) {
            eventDate.setDate(eventDate.getDate() + days);
          } else {
            // Default to 7 days from now if parsing fails
            eventDate.setDate(eventDate.getDate() + 7);
          }
        } else if (dueText === 'Today' || dueText === 'Due today') {
          // Keep today's date
        } else if (dueText.includes('overdue')) {
          // If overdue, use today's date
          eventDate = new Date();
        } else {
          // Default to 7 days from now for unknown formats
          eventDate.setDate(eventDate.getDate() + 7);
        }
      }
      
      // Ensure the date is valid
      if (isNaN(eventDate.getTime())) {
        // If invalid date, default to 7 days from now
        eventDate = new Date();
        eventDate.setDate(eventDate.getDate() + 7);
      }

      // Determine event emoji and class based on status
      let emoji = '📋';
      let className = 'default';
      
      switch (creator.status) {
        case 'awaiting':
          emoji = '📦';
          className = 'awaiting';
          break;
        case 'filming':
          emoji = '🎬';
          className = 'filming';
          break;
        case 'editing':
          emoji = '✂️';
          className = 'editing';
          break;
        case 'review':
          emoji = '👀';
          className = 'review';
          break;
        case 'submitted':
          emoji = '✅';
          className = 'submitted';
          break;
        default:
          emoji = '📋';
          className = 'default';
      }

      // Add overdue indicator
      if (creator.isOverdue) {
        emoji = '⚠️';
        className = 'overdue';
      }

      // Create a more detailed title with campaign and brief info
      let eventTitle = `${emoji} ${creator.creator}`;
      if (creator.campaignName) {
        eventTitle += ` | ${creator.campaignName}`;
      }
      if (creator.briefName) {
        eventTitle += ` → ${creator.briefName}`;
      }
      eventTitle += ` • ${creator.statusText}`;

      calendarEvents.push({
        title: eventTitle,
        date: eventDate.toISOString().split('T')[0],
        classNames: [className],
        campaign: creator.campaignName,
        creator: creator.creator,
        status: creator.statusText,
        brief: creator.briefName,
        progress: creator.progress,
        isOverdue: creator.isOverdue,
        deadline: creator.deadline,
        extendedDeadline: creator.extendedDeadline,
        extendedProps: {
          campaign: creator.campaignName,
          creator: creator.creator,
          status: creator.statusText,
          brief: creator.briefName,
          progress: creator.progress,
          isOverdue: creator.isOverdue,
          deadline: creator.deadline,
          extendedDeadline: creator.extendedDeadline
        }
      });
    });

    return calendarEvents;
  }, [filteredCreatorData]);

  // Group events by due date for the Due Dates tab
  const eventsByDueDate = useMemo(() => {
    const grouped = {};
    
    filteredCreatorData.forEach(creator => {
      let eventDate = new Date();
      
      // Prioritize extendedDeadline, then deadline, then parse due text
      if (creator.extendedDeadline) {
        eventDate = new Date(creator.extendedDeadline);
      } else if (creator.deadline) {
        eventDate = new Date(creator.deadline);
      } else {
        const dueText = creator.due || '';
        if (dueText.includes('Day')) {
          const days = parseInt(dueText.split(' ')[0]);
          if (!isNaN(days)) {
            eventDate.setDate(eventDate.getDate() + days);
          } else {
            eventDate.setDate(eventDate.getDate() + 7);
          }
        } else if (dueText === 'Today' || dueText === 'Due today') {
          // Keep today's date
        } else if (dueText.includes('overdue')) {
          eventDate = new Date();
        } else {
          eventDate.setDate(eventDate.getDate() + 7);
        }
      }
      
      if (isNaN(eventDate.getTime())) {
        eventDate = new Date();
        eventDate.setDate(eventDate.getDate() + 7);
      }
      
      const dateKey = eventDate.toISOString().split('T')[0];
      
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      
      grouped[dateKey].push({
        ...creator,
        dueDate: eventDate,
        dueDateKey: dateKey
      });
    });
    
    // Sort dates
    const sortedDates = Object.keys(grouped).sort((a, b) => new Date(a) - new Date(b));
    
    return sortedDates.map(date => ({
      date,
      dateObj: new Date(date),
      events: grouped[date]
    }));
  }, [filteredCreatorData]);

  // Custom event rendering function
  const renderEventContent = (eventInfo) => {
    const { creator, campaign, brief, status, deadline, extendedDeadline } = eventInfo.event.extendedProps;
    const emoji = eventInfo.event.title.split(' ')[0];
    
    // Format due date
    let dueDateText = '';
    let isOverdue = false;
    if (extendedDeadline || deadline) {
      const effectiveDeadline = extendedDeadline || deadline;
      const deadlineDate = new Date(effectiveDeadline);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      deadlineDate.setHours(0, 0, 0, 0);
      
      const daysDiff = Math.ceil((deadlineDate - today) / (1000 * 60 * 60 * 24));
      
      if (daysDiff < 0) {
        dueDateText = `${Math.abs(daysDiff)}d overdue`;
        isOverdue = true;
      } else if (daysDiff === 0) {
        dueDateText = 'Due today';
      } else if (daysDiff === 1) {
        dueDateText = 'Due tomorrow';
      } else if (daysDiff <= 7) {
        dueDateText = `Due in ${daysDiff}d`;
      } else {
        // Format as date for dates further out
        dueDateText = deadlineDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
    }
    
    return (
      <div className="custom-event-content">
        <div className="event-header">
          <span className="event-emoji">{emoji}</span>
          <span className="event-creator">{creator}</span>
        </div>
        <div className="event-details">
          {campaign && <span className="event-campaign">{campaign}</span>}
          {brief && <span className="event-brief">• {brief}</span>}
        </div>
        {dueDateText && (
          <div className={`event-due-date ${isOverdue ? 'overdue' : ''}`}>{dueDateText}</div>
        )}
        <div className="event-status">{status}</div>
      </div>
    );
  };

  const handleEventClick = (info) => {
    const event = info.event;
    
    // Find the original creator data to get all required fields
    const originalCreator = creatorData.find(creator => 
      creator.creator === event.extendedProps.creator && 
      creator.statusText === event.extendedProps.status
    );
    
    if (originalCreator) {
      setSelectedCreator(originalCreator);
      setShowCreatorModal(true);
    }
  };

  const handleDayClick = (info) => {
    const date = info.dateStr;
    const dayEvents = events.filter(event => event.date === date);
    
    if (dayEvents.length > 2) {
      setSelectedDayEvents(dayEvents);
      setSelectedDate(date);
      setDayEventsModalOpen(true);
    }
  };

  const closeDayEventsModal = () => {
    setDayEventsModalOpen(false);
    setSelectedDayEvents([]);
    setSelectedDate(null);
  };

  // Format due date text
  const formatDueDateText = (dueDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(dueDate);
    date.setHours(0, 0, 0, 0);
    
    const daysDiff = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
    
    if (daysDiff < 0) {
      return `${Math.abs(daysDiff)} days overdue`;
    } else if (daysDiff === 0) {
      return 'Due today';
    } else if (daysDiff === 1) {
      return 'Due tomorrow';
    } else if (daysDiff <= 7) {
      return `Due in ${daysDiff} days`;
    } else {
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  return (
    <div className="full-calendar-wrapper">
      {/* View Tabs */}
      <div className="calendar-view-tabs">
        <button 
          className={`view-tab ${activeView === 'calendar' ? 'active' : ''}`}
          onClick={() => setActiveView('calendar')}
        >
          Calendar
        </button>
        <button 
          className={`view-tab ${activeView === 'dueDates' ? 'active' : ''}`}
          onClick={() => setActiveView('dueDates')}
        >
          Due Dates
        </button>
      </div>

      {/* Calendar Filters */}
      <div className="calendar-filters">
        <div className="filter-group">
          <label htmlFor="campaign-filter" className="filter-label">Campaign</label>
          <select
            id="campaign-filter"
            value={selectedCampaign}
            onChange={(e) => {
              setSelectedCampaign(e.target.value);
              setSelectedBrief(''); // Reset brief when campaign changes
            }}
            className="filter-select"
          >
            <option value="">All Campaigns</option>
            {campaigns.map(campaign => (
              <option key={campaign} value={campaign}>
                {campaign}
              </option>
            ))}
          </select>
        </div>
        
        {selectedCampaign && briefs.length > 0 && (
          <div className="filter-group">
            <label htmlFor="brief-filter" className="filter-label">Brief</label>
            <select
              id="brief-filter"
              value={selectedBrief}
              onChange={(e) => setSelectedBrief(e.target.value)}
              className="filter-select"
            >
              <option value="">All Briefs</option>
              {briefs.map(brief => (
                <option key={brief} value={brief}>
                  {brief}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {activeView === 'calendar' ? (
        <div className="calendar-container">
          <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: ''
          }}
          events={events}
          eventClick={handleEventClick}
          dayCellDidMount={(arg) => {
            const date = arg.dateStr;
            const dayEvents = events.filter(event => event.date === date);
            
            if (dayEvents.length > 2) {
              const cell = arg.el;
              const viewMoreButton = document.createElement('div');
              viewMoreButton.className = 'view-more-events';
              viewMoreButton.innerHTML = `+${dayEvents.length - 2} more`;
              viewMoreButton.onclick = (e) => {
                e.stopPropagation();
                setSelectedDayEvents(dayEvents);
                setSelectedDate(date);
                setDayEventsModalOpen(true);
              };
              cell.appendChild(viewMoreButton);
            }
          }}
          height="auto"
          eventClassNames={(arg) => ['calendar-event', ...arg.event.classNames]}
          eventDisplay="block"
          eventMaxStack={2}
          eventContent={renderEventContent}
        />
        </div>
      ) : (
        <div className="due-dates-list">
          {eventsByDueDate.length === 0 ? (
            <div className="no-due-dates">
              <p>No due dates found</p>
            </div>
          ) : (
            eventsByDueDate.map(({ date, dateObj, events }) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const isOverdue = dateObj < today;
              const isToday = dateObj.getTime() === today.getTime();
              
              return (
                <div key={date} className={`due-date-group ${isOverdue ? 'overdue' : ''} ${isToday ? 'today' : ''}`}>
                  <div className="due-date-header">
                    <h3 className="due-date-title">{formatDueDateText(dateObj)}</h3>
                    <span className="due-date-count">{events.length} {events.length === 1 ? 'assignment' : 'assignments'}</span>
                  </div>
                  <div className="due-date-events">
                    {events.map((creator, index) => {
                      // Determine event emoji and class based on status
                      let emoji = '📋';
                      let className = 'default';
                      
                      switch (creator.status) {
                        case 'awaiting':
                          emoji = '📦';
                          className = 'awaiting';
                          break;
                        case 'filming':
                          emoji = '🎬';
                          className = 'filming';
                          break;
                        case 'editing':
                          emoji = '✂️';
                          className = 'editing';
                          break;
                        case 'review':
                          emoji = '👀';
                          className = 'review';
                          break;
                        case 'submitted':
                          emoji = '✅';
                          className = 'submitted';
                          break;
                        default:
                          emoji = '📋';
                          className = 'default';
                      }
                      
                      if (creator.isOverdue) {
                        emoji = '⚠️';
                        className = 'overdue';
                      }
                      
                      return (
                        <div
                          key={`${creator.id}-${creator.briefId}-${index}`}
                          className={`due-date-event-item ${className}`}
                          onClick={() => {
                            setSelectedCreator(creator);
                            setShowCreatorModal(true);
                          }}
                        >
                          <div className="due-date-event-header">
                            <span className="due-date-event-emoji">{emoji}</span>
                            <span className="due-date-event-creator">{creator.creator || creator.name}</span>
                          </div>
                          <div className="due-date-event-details">
                            {creator.campaignName && (
                              <span className="due-date-event-campaign">{creator.campaignName}</span>
                            )}
                            {creator.briefName && (
                              <span className="due-date-event-brief">• {creator.briefName}</span>
                            )}
                          </div>
                          <div className="due-date-event-status">{creator.statusText || creator.status}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      <CreatorProgressModal
        isOpen={showCreatorModal}
        onClose={() => setShowCreatorModal(false)}
        creatorData={selectedCreator}
      />

      {/* Day Events Modal */}
      {dayEventsModalOpen && (
        <div className="day-events-modal-overlay" onClick={closeDayEventsModal}>
          <div className="day-events-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Events for {selectedDate}</h3>
              <button className="close-button" onClick={closeDayEventsModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="modal-content">
              <div className="events-list">
                {selectedDayEvents.map((event, index) => (
                  <div 
                    key={index} 
                    className={`event-item ${event.classNames.join(' ')}`}
                    onClick={() => {
                      // Find the original creator data
                      const originalCreator = creatorData.find(creator => 
                        creator.creator === event.creator && 
                        creator.statusText === event.status
                      );
                      
                      if (originalCreator) {
                        setSelectedCreator(originalCreator);
                        setShowCreatorModal(true);
                        closeDayEventsModal();
                      }
                    }}
                  >
                    <div className="event-title">{event.title}</div>
                    <div className="event-details">
                      {event.campaign} {event.brief && `• ${event.brief}`}
                    </div>
                    {(() => {
                      const effectiveDeadline = event.extendedDeadline || event.deadline;
                      if (effectiveDeadline) {
                        const deadlineDate = new Date(effectiveDeadline);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        deadlineDate.setHours(0, 0, 0, 0);
                        const daysDiff = Math.ceil((deadlineDate - today) / (1000 * 60 * 60 * 24));
                        let dueDateText = '';
                        if (daysDiff < 0) {
                          dueDateText = `${Math.abs(daysDiff)} days overdue`;
                        } else if (daysDiff === 0) {
                          dueDateText = 'Due today';
                        } else if (daysDiff === 1) {
                          dueDateText = 'Due tomorrow';
                        } else {
                          dueDateText = `Due in ${daysDiff} days`;
                        }
                        return (
                          <div className={`event-due-date-modal ${daysDiff < 0 ? 'overdue' : ''}`}>
                            {dueDateText}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FullCalendarView;
